import {
  ApiNote,
  useGetByTenantClientQuery,
  useGetByTenantNoteQuery,
  usePostByTenantNoteInstructionSubmitMutation
} from 'api/rtk-api'
import { Alert, LabelValueRow, OperationButton, Rule } from 'components/core-v2/atoms'
import {
  CheckboxField,
  CurrencyFieldV2,
  FormError,
  RadioFieldSet
} from 'components/core-v2/molecules'
import { required } from 'components/core-v2/molecules/fields/validators'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import { formatCurrencyFull } from 'utilities/currency'
import { unformatNumberString } from 'utilities/string'

import { CURRENCY, PATH, TENANT } from '../../../../../const'
import { ClassificationCard } from './classification-card'
import classes from './index.module.scss'
import { TradeTable } from './trade-table'

interface Fields {
  [key: string]: string
  amountType: 'equal' | 'prorated' | 'custom'
  group: null
  totalAmount?: string
  type: 'top-up' | 'withdraw'
}

interface Props {
  note: ApiNote
}
const Body = ({ note }: Props) => {
  const navigate = useNavigate()
  const form = useForm<Fields>({
    defaultValues: {
      type: 'top-up',
      amountType: 'equal',
      totalAmount: null
    },
    shouldUnregister: true,
    criteriaMode: 'all'
  })

  const typeValue = form.watch('type')
  const amountTypeValue = form.watch('amountType')
  const totalAmountValue = form.watch('totalAmount')

  const [showOver95PercentWarning, setShowOver95PercentWarning] = useState(false)

  const noteValue = note.associatedTrades.reduce((sum, trade) => trade.accountValue + sum, 0)

  const [submit, { error, isError, isLoading: isSubmitLoading, isSuccess }] =
    usePostByTenantNoteInstructionSubmitMutation()

  const onSubmit = (formValues: any) => {
    const trades = note.associatedTrades

    const { amountType, type } = formValues
    const totalAmount = unformatNumberString(formValues.totalAmount)

    let accountAmounts = {}
    if (amountType === 'equal') {
      const equalAmount = totalAmount / trades.length
      accountAmounts = trades.reduce((acc, trade) => {
        acc[trade.accountId] = equalAmount
        return acc
      }, {})
    }
    if (amountType === 'prorated') {
      accountAmounts = trades.reduce((acc, trade) => {
        const weight = trade.accountValue / noteValue
        const weightedTopUpAmount = totalAmount * weight
        acc[trade.accountId] = weightedTopUpAmount
        return acc
      }, {})
    }
    if (amountType === 'custom') {
      const tradeValues = Object.entries(formValues).filter(
        ([key]) =>
          key !== 'amountType' && key !== 'type' && key !== 'totalAmount' && key !== 'confirmation'
      )
      const unformattedTradeValues = tradeValues.map(([tradeId, value]) => [
        tradeId,
        unformatNumberString(value as string)
      ])
      accountAmounts = Object.fromEntries(unformattedTradeValues)
    }
    submit({
      tenant: TENANT,
      apiNoteInstructionSubmission: {
        noteId: note.noteId,
        instructionType: type === 'top-up' ? 'Deposit' : 'Withdrawal',
        accountAmounts
      }
    })
  }

  const isTopUp = typeValue === 'top-up'
  const isWithdrawal = typeValue === 'withdraw'
  const typeLabel = isTopUp ? 'top up' : 'withdrawal'

  useEffect(() => {
    if (isSuccess) {
      toast.success(`Note ${typeLabel} successful`)
      navigate(PATH.reportsNotes)
    }
  }, [isSuccess, navigate, typeLabel])

  const showTotal = amountTypeValue === 'equal' || amountTypeValue === 'prorated'
  const totalAmountLabel = `Total ${typeLabel} amount`

  const { data: clientData, isLoading } = useGetByTenantClientQuery({ tenant: TENANT })

  const clientSettings =
    !isLoading && clientData
      ? clientData.find(client => {
          const containsBusinessUnit = client.businessUnits.find(
            businessUnit => businessUnit.businessUnitId === note.businessUnitId
          )

          return Boolean(containsBusinessUnit)
        }).tradeBuildingSettings
      : null

  const minimumTopUpNumber = clientSettings?.minimumTopUpSize
  const minimumNoteSizeNumber = clientSettings?.minimumNoteSize
  const minimumWithdrawalNumber = clientSettings?.minimumWithdrawalSize

  const minimumTopUpValue = formatCurrencyFull(minimumTopUpNumber, CURRENCY)
  const minimumNoteSizeValue = formatCurrencyFull(minimumNoteSizeNumber, CURRENCY)
  const minimumWithdrawalValue = formatCurrencyFull(minimumWithdrawalNumber, CURRENCY)

  const totalNoteValue = note.associatedTrades.reduce((sum, trade) => sum + trade.accountValue, 0)

  // Form wide validation
  useEffect(() => {
    const minimumTopUpError = {
      type: 'minTopup',
      message: `All requested top ups must be above the minimum top up amount of ${minimumTopUpValue}`
    }

    const minimumWithdrawalError = {
      type: 'minWithdrawal',
      message: `All requested withdrawals must be above the minimum withdrawal amount of ${minimumWithdrawalValue}`
    }
    const minimumNoteValueError = {
      type: 'minNoteValue',
      message: `The note cannot fall below the minimum note size of ${minimumNoteSizeValue}`
    }
    const withdrawalTooHighError = {
      type: 'withdrawalTooHigh',
      message: `The withdrawal amount for one or more trades is greater than its current value. Please enter an amount which is less than the current value`
    }
    const over95PercentWithdrawalError = {
      type: 'over95PercentWithdrawal',
      message: 'Withdrawing over 95% of all funds will result in the trade closing'
    }

    const totalAmount = unformatNumberString(totalAmountValue)

    let hasSetError = false
    let hasSetWarning = false

    if (amountTypeValue !== 'custom' && !totalAmountValue) {
      form.clearErrors('group')
      return
    }

    // Minimum note size
    if (typeValue === 'withdraw' && totalNoteValue - totalAmount < minimumNoteSizeNumber) {
      form.setError('group', minimumNoteValueError)
      hasSetError = true
    }

    if (amountTypeValue === 'equal') {
      const amountPerTrade = totalAmount / note.associatedTrades.length
      if (typeValue === 'top-up') {
        if (amountPerTrade < minimumTopUpNumber) {
          // Minimum top-up
          form.setError('group', minimumTopUpError)
          hasSetError = true
        }
      }
      if (typeValue === 'withdraw') {
        // Minimum withdrawal
        if (amountPerTrade < minimumWithdrawalNumber) {
          form.setError('group', minimumWithdrawalError)
          hasSetError = true
        }

        note.associatedTrades.forEach(trade => {
          // Withdrawal over 95% the value of a single trade
          if (amountPerTrade / trade.accountValue > 0.95) {
            setShowOver95PercentWarning(true)
            hasSetWarning = true
          }
          // Withdrawal more than a given trade
          if (amountPerTrade > trade.accountValue) {
            form.setError('group', withdrawalTooHighError)
            hasSetError = true
          }
        })
      }
    } else if (amountTypeValue === 'prorated') {
      note.associatedTrades.forEach(trade => {
        const weight = trade.accountValue / totalNoteValue
        const weightedAmount = unformatNumberString(totalAmountValue) * weight
        if (typeValue === 'top-up') {
          // Minimum top-up
          if (weightedAmount < minimumTopUpNumber) {
            form.setError('group', minimumTopUpError)
            hasSetError = true
          }
        }
        if (typeValue === 'withdraw') {
          // Minimum withdrawal
          if (weightedAmount < minimumWithdrawalNumber) {
            form.setError('group', minimumWithdrawalError)
            hasSetError = true
          }
          // Withdrawal over 95% the value of a single trade
          if (weightedAmount / trade.accountValue > 0.95) {
            form.setError('group', over95PercentWithdrawalError)
            hasSetError = true
          }
          // Withdrawal more than a given trade
          if (weightedAmount > trade.accountValue) {
            form.setError('group', withdrawalTooHighError)
            hasSetError = true
          }
        }
      })
    }

    if (!hasSetError) {
      form.clearErrors('group')
    }
    if (!hasSetWarning) {
      setShowOver95PercentWarning(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    amountTypeValue,
    form.clearErrors,
    form.setError,
    minimumNoteSizeNumber,
    minimumNoteSizeValue,
    minimumTopUpNumber,
    minimumTopUpValue,
    minimumWithdrawalNumber,
    minimumWithdrawalValue,
    note.associatedTrades,
    note.associatedTrades.length,
    totalAmountValue,
    totalNoteValue,
    typeValue
  ])

  useEffect(() => {
    form.setValue('totalAmount', '')
    note.associatedTrades.forEach(trade => form.setValue(trade.accountId, ''))
    setShowOver95PercentWarning(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeValue])

  const errors = Object.entries(form.formState.errors).filter(([key]) => key !== 'confirmation')
  const isDisabled = errors.length !== 0 || !form.formState.isValid

  return (
    <div className={classes.root}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <RadioFieldSet
            legend="Choose an option"
            name="type"
            options={[
              { label: 'Top up', value: 'top-up' },
              { label: 'Withdraw', value: 'withdraw' }
            ]}
          />

          <RadioFieldSet
            className="mt-6"
            legend={`How do you want to ${
              isTopUp ? 'top up' : 'withdraw from'
            } the trades in this note?`}
            name="amountType"
            options={[
              { label: 'All by the same amount', value: 'equal' },
              { label: 'Prorated across trades', value: 'prorated' },
              { label: 'Custom amounts', value: 'custom' }
            ]}
          />

          <LabelValueRow
            className="mt-8"
            label="Current note value"
            value={formatCurrencyFull(noteValue, CURRENCY)}
          />

          {isTopUp && (
            <LabelValueRow
              showBottomBorder
              isLoading={isLoading}
              label="Minimum trade top up amount"
              value={minimumTopUpValue}
            />
          )}

          {isWithdrawal && (
            <>
              <LabelValueRow
                isLoading={isLoading}
                label="Minimum note value"
                value={minimumNoteSizeValue}
              />
              <LabelValueRow
                isLoading={isLoading}
                label="Minimum trade withdrawal amount"
                value={minimumWithdrawalValue}
              />
            </>
          )}

          {showTotal && (
            <div className={classes.totalAmountField}>
              <CurrencyFieldV2 id="totalAmount" label={totalAmountLabel} validators={required} />
            </div>
          )}

          <Rule className="my-10" />

          <TradeTable
            clientSettings={clientSettings}
            note={note}
            setShowOver95PercentWarning={setShowOver95PercentWarning}
            showOver95PercentWarning={showOver95PercentWarning}
          />

          <ClassificationCard note={note} />

          <FormError collapseClassName="mt-4" isShowing={isError}>
            {error &&
              'data' in error &&
              (error.data as string[]).map(error => (
                <p key={error} className="mb-0">
                  {error}
                </p>
              ))}
          </FormError>

          <CheckboxField
            className={classes.confirmationField}
            id="confirmation"
            label={`Please confirm the ${typeLabel} before submitting the trade for approval`}
            validators={{
              required: 'Please confirm before submitting'
            }}
          />

          <OperationButton
            className="my-8"
            isDisabled={isDisabled}
            isLoading={isSubmitLoading}
            operationType="progress"
            type="submit"
          >
            Submit {typeLabel}
          </OperationButton>
        </form>
      </FormProvider>
    </div>
  )
}

interface FormProps {
  isClosed: boolean
  isLocked: boolean
  isLockedForCollateral: boolean
}
export const TopUpWithdrawNoteForm = ({ isClosed, isLocked, isLockedForCollateral }: FormProps) => {
  const { data: notes, isLoading } = useGetByTenantNoteQuery({
    tenant: TENANT
  })
  const { noteId } = useParams()

  if (isClosed)
    return (
      <Alert type="warning">
        This action is not available because all trades within the note have been closed.
      </Alert>
    )

  if (isLockedForCollateral) {
    return (
      <Alert type="warning">
        This action cannot be performed, because this note is used as collateral for a loan
      </Alert>
    )
  }

  if (isLocked)
    return (
      <Alert type="warning">
        One or more of the trades in the note has instructions awaiting approval
      </Alert>
    )

  if (isLoading) return null

  const note = !noteId ? null : notes.find(note => note.noteId === noteId)

  if (!note) return null

  return <Body note={note} />
}
